import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { SparklesIcon, CheckIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { MarkdownEditor } from './markdown-editor';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/clients';
import { cn } from '@/lib/utils';
import { collaboratorsQuery } from '@/services/repo';

interface ComparisonState {
  isComparing: boolean;
  originalText: string;
  suggestedText: string;
}

export const DescriptionEditorWithCompare = ({
  description,
  updateDescription,
  className,
}: {
  description: string;
  updateDescription: ({ body }: { body: string }) => void;
  className?: string;
}) => {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { data: mentionCollaborators } = useQuery(collaboratorsQuery(owner, repo));

  const [isGenerating, setIsGenerating] = useState(false);
  const [comparison, setComparison] = useState<ComparisonState>({
    isComparing: false,
    originalText: '',
    suggestedText: '',
  });

  const hasDescription = description && description.trim().length > 0;

  const handleChange = (value: string) => {
    if (value !== description) {
      updateDescription({ body: value });
    }
  };

  const generateDescription = async () => {
    setIsGenerating(true);
    try {
      const response = await api.github[':owner'][':repo']['pulls'][':pull_number']['describe'].$post({
        param: {
          owner,
          repo,
          pull_number: number.toString(),
        },
      });
      const result = await response.json();

      if (result.description) {
        // If current description is empty, directly apply the AI-generated description
        if (!description || description.trim().length === 0) {
          updateDescription({ body: result.description });
        } else {
          // Show comparison view when there's existing content
          setComparison({
            isComparing: true,
            originalText: description || '',
            suggestedText: result.description,
          });
        }
      }
    } catch (error) {
      console.error('Error generating PR description:', error);
      toast.error('Failed to generate PR description.');
    } finally {
      setIsGenerating(false);
    }
  };

  const acceptChanges = () => {
    updateDescription({ body: comparison.suggestedText });
    setComparison((prev) => ({ ...prev, isComparing: false }));
  };

  const rejectChanges = () => {
    setComparison((prev) => ({ ...prev, isComparing: false }));
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Comparison overlay - shows on top when comparing */}
      {comparison.isComparing && (
        <div className="space-y-4">
          {/* Comparison view */}
          <div className="space-y-4">
            {/* Current content */}
            <div className="border border-red-200 rounded-lg overflow-hidden bg-red-50">
              <div className="px-3 py-2 border-b border-red-200 bg-red-100">
                <span className="text-sm font-medium text-red-800">Current</span>
              </div>
              <div className="p-4">
                {comparison.originalText ? (
                  <MarkdownEditor value={comparison.originalText} editable={false} owner={owner} />
                ) : (
                  <p className="text-red-600 italic">No description</p>
                )}
              </div>
            </div>

            {/* Suggested content */}
            <div className="border border-green-200 rounded-lg overflow-hidden bg-green-50">
              <div className="px-3 py-2 border-b border-green-200 bg-green-100">
                <span className="text-sm font-medium text-green-800">Suggestion</span>
              </div>
              <div className="p-4">
                <MarkdownEditor value={comparison.suggestedText} editable={false} owner={owner} />
              </div>
            </div>

            {/* Comparison footer */}
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={rejectChanges} className="text-xs">
                  <XIcon size={14} className="mr-1" />
                  Reject
                </Button>
                <Button size="sm" onClick={acceptChanges} className="text-xs">
                  <CheckIcon size={14} className="mr-1" />
                  Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor - always mounted but hidden during comparison */}
      <div className={comparison.isComparing ? 'hidden' : ''}>
        <MarkdownEditor
          key="editor"
          value={description}
          onBlur={({ editor }) => handleChange(editor.storage.markdown.getMarkdown())}
          placeholder="Add a pull request description..."
          mentionCollaborators={mentionCollaborators}
          owner={owner}
          repo={repo}
          showBubbleMenu
        />
      </div>

      {/* Generate button - always show when available */}
      {!comparison.isComparing && (
        <div className="mt-4 flex justify-start">
          <Button
            variant={hasDescription ? 'outline' : 'default'}
            size="sm"
            disabled={isGenerating}
            onClick={generateDescription}
            className={cn(
              hasDescription
                ? 'text-stone-600 bg-stone-100 hover:bg-stone-200'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            )}
          >
            {isGenerating ? <SparklesIcon size={16} className="animate-pulse" /> : <SparklesIcon size={16} />}
            <span>{isGenerating ? 'Generating...' : hasDescription ? 'Rewrite' : 'Generate Description'}</span>
          </Button>
        </div>
      )}
    </div>
  );
};
